import { useCallback, useMemo, useState } from 'react';

// `label` travels with each entry so the breadcrumb never
// has to re-look-up a name from an id after the underlying data changes.
export type DrillLevel = 'site' | 'cage' | 'fishGroups';

export interface DrillPathEntry {
  level: DrillLevel;
  id: string;
  label: string;
}

// Drill state stays local to the page and does not use router state or context.
export const useDrillPath = () => {
  const [path, setPath] = useState<DrillPathEntry[]>([]);

  const push = useCallback((entry: DrillPathEntry) => setPath((current) => [...current, entry]), []);

  // Breadcrumb clicks jump straight to any earlier level; `index` is the
  // breadcrumb's own position, so `-1` (the "root" crumb) truncates back to
  // an empty path.
  const popTo = useCallback((index: number) => setPath((current) => current.slice(0, index + 1)), []);

  const back = useCallback(() => setPath((current) => current.slice(0, -1)), []);

  const reset = useCallback(() => setPath([]), []);

  const current = useMemo(() => path[path.length - 1], [path]);

  return { path, current, push, popTo, back, reset };
};
